import {useState, useEffect} from "react";
import {get, getWithParams, post, put} from "../../utils/fetchAPI";
import Select from "react-select";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";

// A factory, not a shared constant: the nested role/specialty objects would
// otherwise be aliased into every form reset.
const makeInitialFormData = () => ({
    user: '',
    organization: '',
    location: '',
    role: {
        code: '',
        display: ''
    },
    parentUsers: [],
    specialty: [
        {
            speciality: '',
            code: '',
            display: ''
        }
    ],
    status: '',
    medicinePriority: false,
    manufacturerName: '',
    sponsor: false,
    perDayLimit: 1000,
    fee: 0,
})

const UserRoleForm = ({user, userRole, onAddLocation, onAddLocationSuccess, onClose}) => {
    // `userRole` present => edit an existing role; absent => create a new one.
    const isEdit = Boolean(userRole);

    const [formData, setFormData] = useState(makeInitialFormData);
    const [organizations, setOrganizations] = useState([]);
    // const [allLocations, setAllLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [specialities, setSpecialities] = useState([]);
    // react-select needs {value,label} objects, but formData.parentUsers holds ids,
    // so the chosen doctors are tracked separately for display.
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);

    //organization list fetching
    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await get(`organization/list?per_page=${perPage}&current_page=${currentPage}`);
                console.log("orglist", response); // Debug the full response
                setOrganizations(response?.data?.organizations || []); // Access the correct field
            } catch (error) {
                console.error("Error fetching organizations:", error);
            }
        };

        fetchOrganizations();
    }, []);

    // active specialities for the dropdown, from the admin-managed master list
    useEffect(() => {
        const fetchSpecialities = async () => {
            try {
                const response = await getWithParams('/speciality/list', {
                    active: 'true',
                    per_page: 200,
                    current_page: 1,
                });
                setSpecialities(response?.data?.specialities || []);
            } catch (error) {
                console.error("Error fetching specialities:", error);
            }
        };

        fetchSpecialities();
    }, []);

    const loadDoctors = async (inputValue) => {
        try {
            const res = await get(
                `all-user/list?per_page=${perPage}&search=${inputValue || ""}`
            );

            return (
                res?.data?.users?.map(doc => ({
                    value: doc._id,
                    label: doc.name,
                })) || []
            );
        } catch (error) {
            console.error(error);
            return [];
        }
    };



    //fetch org for select field
    const orgOptions = organizations.map(org => ({
        value: org._id,
        label: org.name
    }));
    //fetch location for select field
    const locOptions = filteredLocations.map(loc => ({
        value: loc._id,
        label: loc.name
    }));
    const specialityOptions = specialities.map(sp => ({
        value: sp._id,
        label: sp.name,
        code: sp.code
    }));

    // The org/location lists are paginated, so a role's saved org or location may
    // not be among the fetched options — fall back to the populated name from the
    // role itself so edit mode still shows what is stored.
    const orgValue = orgOptions.find(o => o.value === formData.organization)
        || (userRole?.organization?.name && (userRole.organization._id === formData.organization)
            ? {value: formData.organization, label: userRole.organization.name}
            : null);
    const locValue = locOptions.find(o => o.value === formData.location)
        || (userRole?.location?.name && (userRole.location._id === formData.location)
            ? {value: formData.location, label: userRole.location.name}
            : null);

    // Match the saved speciality by ref first, then by code for rows saved before
    // the dropdown existed. If neither matches, show the stored free text rather
    // than a blank box so the admin can see what is being replaced.
    const specialityValue = (() => {
        const saved = formData.specialty?.[0];
        if (!saved) return null;
        return specialityOptions.find(o => o.value === saved.speciality)
            || specialityOptions.find(o => saved.code && o.code === saved.code)
            || (saved.display ? {value: '', label: saved.display, code: saved.code} : null);
    })();

    // Create mode just seeds the user id; edit mode prefills every field from the
    // role being edited (list endpoint populates organization/location/parentUsers).
    useEffect(() => {
        if (!userRole) {
            setFormData({...makeInitialFormData(), user: user?._id || ''});
            setSelectedDoctors([]);
            setFilteredLocations([]);
            return;
        }

        const organizationId = userRole.organization?._id || userRole.organization || '';
        const savedSpecialty = userRole.specialty?.[0];

        setFormData({
            user: userRole.user?._id || userRole.user || user?._id || '',
            organization: organizationId,
            location: userRole.location?._id || userRole.location || '',
            parentUsers: (userRole.parentUsers || []).map(p => p?._id || p),
            role: {
                code: userRole.role?.code || '',
                display: userRole.role?.display || ''
            },
            specialty: [{
                speciality: savedSpecialty?.speciality?._id || savedSpecialty?.speciality || '',
                code: savedSpecialty?.code || '',
                display: savedSpecialty?.display || ''
            }],
            status: userRole.status || '',
            medicinePriority: Boolean(userRole.medicinePriority),
            manufacturerName: userRole.manufacturerName || '',
            sponsor: Boolean(userRole.sponsor),
            perDayLimit: userRole.perDayLimit ?? 1000,
            fee: userRole.fee ?? 0,
        });
        setSelectedDoctors((userRole.parentUsers || []).map(p => ({
            value: p?._id || p,
            label: p?.name || p?._id || p,
        })));
        loadLocations(organizationId);
    }, [user, userRole]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleLocationChange = (e) => {
        const {value} = e;
        setFormData((prevData) => ({
            ...prevData,
            location: value,
        }));
    }

    const handleDoctorsChange = (selectedOptions) => {
        setSelectedDoctors(selectedOptions || []);
        setFormData(prev => ({
            ...prev,
            parentUsers: selectedOptions?.map(opt => opt.value) || [],
        }));
    };

    const handleSpecialityChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            specialty: selectedOption
                ? [{
                    speciality: selectedOption.value,
                    code: selectedOption.code,
                    display: selectedOption.label,
                }]
                : [{speciality: '', code: '', display: ''}],
        }));
    };

    const loadLocations = async (organizationId) => {
        if (!organizationId) {
            setFilteredLocations([]);
            return;
        }
        try {
            const response = await get(`location/list/${organizationId}`);
            setFilteredLocations(response.data.locations || []);
        } catch (error) {
            console.error("Error fetching locations:", error);
            setFilteredLocations([]); // Clear locations on error
        }
    };

    const handleOrganizationChange = async (selectedOption) => {
        const organizationId = selectedOption.value;

        setFormData((prevData) => ({...prevData, organization: organizationId, location: ''}));
        await loadLocations(organizationId);
    };

    const handleNestedChange = (e, path) => {
        const {name, value} = e.target;
        setFormData((prevData) => {
            let newData = {...prevData};
            path.reduce((acc, key, index) => {
                if (index === path.length - 1) {
                    acc[key] = value;
                } else {
                    acc[key] = {...acc[key]};
                }
                return acc[key];
            }, newData);
            return newData;
        });
    };

    // specialty[].speciality is an ObjectId path, so an empty string must never
    // be sent — it fails Mongoose's cast and the whole save is rejected.
    const buildSpecialtyPayload = () => {
        const saved = formData.specialty?.[0] || {};
        // keep the stored ref; else adopt the option matched by code — this also
        // auto-upgrades legacy free-text rows to carry the ref once re-saved
        const ref = saved.speciality || specialityValue?.value || '';
        const entry = {};
        if (ref) entry.speciality = ref;
        if (saved.code) entry.code = saved.code;
        if (saved.display) entry.display = saved.display;
        return Object.keys(entry).length ? [entry] : [];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // react-select fields can't use the native `required` attribute
        if (!isEdit && (!formData.organization || !formData.location)) {
            toast.error('Organization and location are required!', {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        const payload = { ...formData, specialty: buildSpecialtyPayload() };
        // ObjectId paths reject empty strings — omit them instead. On update,
        // findOneAndUpdate leaves omitted keys untouched.
        ['user', 'organization', 'location'].forEach((key) => {
            if (!payload[key]) delete payload[key];
        });

        try {
            const response = isEdit
                ? await put(`user/role/update/${userRole._id}`, payload)
                : await post('user/role/create', payload);
            if (response.status === 200 || response.status === 201) {
                onAddLocation();
                onAddLocationSuccess(isEdit);
            }
        } catch (error) {
            console.error("Error:", error);
            const errorMessage = error.response?.data?.errors?.[0]?.msg ||
                error.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} role. Please try again.`;
            toast.error(errorMessage, {
                position: "top-center",
                theme: "light",
            });
        }
    };


    return (
        <div className='p-8 max-w-7xl'>
            <h2 className="text-2xl font-bold text-blue-500 mb-6">{isEdit ? "Edit Role" : "Add Role"}</h2>
            <form onSubmit={handleSubmit} className="text-black flex flex-col w-full">
                <div className="flex flex-wrap max-h-[30rem] overflow-y-auto">
                    {/* Left Column */}
                    <div className="w-full md:w-1/2 pr-4 space-y-6">
                        {/* Organization Select */}
                        <div className="flex flex-col">
                            <label className="text-gray-700 font-semibold mb-1 text-left">Organization <span
                                className="text-red-500">*</span></label>
                            <Select
                                options={orgOptions}
                                value={orgValue}
                                onChange={handleOrganizationChange}
                                placeholder="Search organization..."
                                className="w-full"
                                isSearchable
                            />
                        </div>

                        {/* Filtered Location Select */}
                        <div className="flex flex-col">
                            <label className="text-gray-700 font-semibold mb-1 text-left">Location <span
                                className="text-red-500">*</span></label>
                            <Select
                                options={locOptions}
                                value={locValue}
                                onChange={handleLocationChange}
                                placeholder="Search location..."
                                className="w-full"
                                isSearchable
                            />
                        </div>
                        <div>
                            <label className="text-gray-700 font-semibold">Status <span
                                className="text-red-500">*</span></label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                required
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                            >
                                <option value="">Select Status</option>
                                <option value="active">Active</option>
                                <option value="deactive">Deactive</option>

                            </select>
                        </div>
                        <div>
                            <label className="text-gray-700 font-semibold">Fee</label>
                            <input
                                type="number"
                                name="fee"
                                value={formData.fee}
                                onChange={handleChange}
                                className="p-2 mt-1 border rounded-md w-full"
                            />
                        </div>
                        <div>
                            <label className="text-gray-700 font-semibold">Per Day Limit</label>
                            <input
                                type="number"
                                name="perDayLimit"
                                value={formData.perDayLimit}
                                onChange={handleChange}
                                className="p-2 mt-1 border rounded-md w-full"
                            />
                        </div>
                        <div className="flex items-center space-x-3">
                            <label className="text-gray-700 font-semibold hidden">Medicine Priority</label>
                            <input
                                type="checkbox"
                                checked={formData.medicinePriority}
                                onChange={(e) => setFormData({...formData, medicinePriority: e.target.checked})}
                                hidden
                                className="h-5 w-5 text-blue-600"
                            />
                        </div>
                        <div>
                            <label className="text-gray-700 font-semibold hidden">Manufacturer Name</label>
                            <input
                                name="manufacturerName"
                                value={formData.manufacturerName}
                                onChange={handleChange}
                                hidden
                                className="p-2 mt-1 border rounded-md w-full"
                                placeholder="Enter Manufacturer Name"
                            />
                        </div>
                        <div className="flex items-center space-x-3">
                            <label className="text-gray-700 font-semibold hidden">Sponsor</label>
                            <input
                                type="checkbox"
                                checked={formData.sponsor}
                                onChange={(e) => setFormData({...formData, sponsor: e.target.checked})}
                                hidden
                                className="h-5 w-5 text-blue-600"
                            />
                        </div>
                        <div>
                            {/* <label className="text-gray-700 font-semibold">User ID</label> */}
                            <input
                                name="user"
                                value={formData.user}
                                onChange={handleChange}
                                className="p-2 mt-1 border rounded-md hidden"
                                placeholder="Enter User ID"
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="w-full md:w-1/2 pl-4 space-y-6">
                        <div>
                            <label className="text-gray-700 font-semibold">Role Code</label>
                            <select
                                name="code"
                                value={formData.role.code}
                                onChange={(e) => handleNestedChange(e, ["role", "code"])}
                                required
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                            >
                                <option value="">Select Role</option>
                                <option value="doctor">Doctor</option>
                                <option value="staff">Staff</option>
                                <option value="assistant">Assistant</option>

                            </select>
                        </div>
                        {(formData.role.code === "staff" || formData.role.code === "assistant") && (
                            <div className="flex flex-col">
                                <label className="text-gray-700 font-semibold mb-1 text-left">
                                    Doctors <span className="text-red-500">*</span>
                                </label>

                                <AsyncSelect
                                    isMulti
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadDoctors}
                                    value={selectedDoctors}
                                    onChange={handleDoctorsChange}
                                    placeholder="Search doctors..."
                                    className="w-full"
                                    closeMenuOnSelect={false}
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-gray-700 font-semibold">Role Display</label>
                            <input
                                name="display"
                                value={formData.role.display}
                                onChange={(e) => handleNestedChange(e, ["role", "display"])}
                                className="p-2 mt-1 border rounded-md w-full"
                                placeholder="Enter Role Display"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-gray-700 font-semibold mb-1 text-left">Speciality</label>
                            <Select
                                options={specialityOptions}
                                value={specialityValue}
                                onChange={handleSpecialityChange}
                                placeholder="Search speciality..."
                                className="w-full"
                                isSearchable
                                isClearable
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-5 mt-6">
                    <button
                        type="submit"
                        className="text-sm px-4 py-2 bg-primary text-white rounded-md"
                    >
                        {isEdit ? "Update Role" : "Add Role"}
                    </button>
                    <button type="button" onClick={onClose}
                            className="text-sm px-4 py-2 bg-secondary text-white rounded-md">Cancel
                    </button>
                </div>
            </form>
        </div>
    )
}
export default UserRoleForm